import { Box, Button, Typography } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReminderHighlight } from 'src/components/atoms/reminder-highlight';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useDeleteBookingRules } from 'src/features/booking-rules/api/hooks/use-delete-booking-rules';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';

interface Props extends DialogActions {
  token: string;
  name: string;
  employeesGroupsTokens: string[];
}

export const DeleteBookingRulesDialog = ({
  open,
  onCancel,
  token,
  name,
  employeesGroupsTokens,
}: Props) => {
  const { t } = useTranslation(['bookingRules', 'common']);

  const { results } = useEmployeesGroupsByTokens(employeesGroupsTokens);

  const { mutate: deleteBookingRules, isPending: isDeletingBookingRules } =
    useDeleteBookingRules(onCancel);

  let content: ReactNode = (
    <Typography variant="body2">
      {t(
        'bookingRules:common.deleteBookingRulesDialog.contentWithoutEmployeeGroups',
        { name },
      )}
    </Typography>
  );

  if (employeesGroupsTokens.length > 0) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ReminderHighlight>
          <Typography variant="body2">
            {t(
              'bookingRules:common.deleteBookingRulesDialog.contentWithEmployeeGroups.cautionMessage',
            )}
          </Typography>
        </ReminderHighlight>

        <Typography variant="body2">
          {t(
            'bookingRules:common.deleteBookingRulesDialog.contentWithEmployeeGroups.description',
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {results?.hits?.map(group => (
            <Button
              key={group.token}
              variant="outlined"
              component={Link}
              to={`/tenant/employees/groups/${group.token}/booking-rules`}
              target="_blank">
              {group.name}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      variant="warning"
      open={open}
      title={t('bookingRules:common.deleteBookingRulesDialog.title')}
      onCancel={onCancel}
      onConfirm={() => deleteBookingRules(token)}
      cancelButtonText={t('common:no')}
      confirmButtonText={t(
        'bookingRules:common.deleteBookingRulesDialog.contentWithEmployeeGroups.confirmButtonText',
      )}
      isConfirmButtonDisabled={employeesGroupsTokens.length > 0}
      isConfirmButtonSubmitting={isDeletingBookingRules}>
      {content}
    </Dialog>
  );
};
